import React, { useState } from 'react';
import { clsx } from 'clsx';
import { format } from 'date-fns';
import { 
  HiClock,
  HiEnvelope as HiMail,
  HiChatBubbleLeft as HiChat,
  HiPhone,
  HiGlobeAlt,
  HiCog,
  HiPaperClip,
  HiChevronRight,
  HiChevronDown
} from 'react-icons/hi2';
import { Avatar } from '@/components/Avatar';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { RichTextRenderer } from '@/components/editor/RichTextRenderer';
import { TicketStatus, TicketPriority, TicketSource } from '@/types/api';
import type { TicketInboxRowProps } from '@/types/ui';
import { resolveDisplayTimestamp } from '@/utils/ticketTimestamp';

/**
 * Enhanced Ticket Inbox Row Component - Gmail/Outlook Style Interface
 * Provides familiar email-like experience for agents
 */
export const TicketInboxRow: React.FC<TicketInboxRowProps> = ({
  ticket,
  selected = false,
  onSelect,
  onClick,
  unread = false,
  showPreview = true,
  showAttachmentIndicator = true,
  threadingEnabled = false,
  relatedTickets = [],
  className,
  'data-testid': testId,
  ...props
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Priority color mapping with more subtle approach for inbox view (BH#56)
  const getPriorityIndicator = (priority: TicketPriority) => {
    const colorClasses = {
      [TicketPriority.Urgent]: 'bg-red-500',
      [TicketPriority.High]: 'bg-orange-500', 
      [TicketPriority.Medium]: 'bg-yellow-500',
      [TicketPriority.Low]: 'bg-green-500'
    };

    const colorClass = colorClasses[priority] || 'bg-gray-400';
    return (
      <div 
        className={clsx('w-2.5 h-2.5 rounded-full', colorClass)}
        title={`${priority || 'Normal'} Priority`}
      />
    );
  };

  // Source icon mapping
  const getSourceIcon = (source: TicketSource) => {
    const iconClass = "w-4 h-4 text-gray-400";
    switch (source) {
      case TicketSource.Email: return <HiMail className={iconClass} />;
      case TicketSource.Chat: return <HiChat className={iconClass} />;
      case TicketSource.Phone: return <HiPhone className={iconClass} />;
      case TicketSource.WebForm: return <HiGlobeAlt className={iconClass} />;
      case TicketSource.API: return <HiCog className={iconClass} />;
      default: return <HiGlobeAlt className={iconClass} />;
    }
  };

  // Format time for inbox display (Gmail-style). Falls back to createdAt when
  // updatedAt is null so never-edited tickets show their real date, not 1/1/70.
  const formatInboxTime = (dateString: string | null) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '—';
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    // Today - show time
    if (diffInHours < 24) {
      return format(date, 'h:mm a');
    }
    
    // This year - show month and day
    if (date.getFullYear() === now.getFullYear()) {
      return format(date, 'MMM d');
    }
    
    // Previous years - show year
    return format(date, 'dd/MM/yy');
  };

  const handleRowClick = (e: React.MouseEvent) => {
    // Don't trigger row click if clicking on interactive elements
    if (
      (e.target as HTMLElement).closest('button') ||
      (e.target as HTMLElement).closest('input') ||
      (e.target as HTMLElement).closest('[role="button"]')
    ) {
      return;
    }
    
    onClick?.(ticket);
  };

  const handleSelectToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect?.(ticket.id, !selected);
  };

  const handleExpandToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  const getStatusBadgeVariant = (status: TicketStatus) => {
    switch (status) {
      case TicketStatus.Open: return 'info';
      case TicketStatus.InProgress: return 'warning';
      case TicketStatus.Resolved: return 'success';
      case TicketStatus.Closed: return 'secondary';
      default: return 'secondary';
    }
  };

  // updatedAt is nullable; display it when valid, fall back to createdAt, else
  // admit there is no date rather than rendering 1/1/70 (epoch) or "just now".
  const resolvedTimestamp = resolveDisplayTimestamp(ticket.updatedAt, ticket.createdAt);
  const resolvedTimestampLabel = resolvedTimestamp
    ? resolvedTimestamp === ticket.updatedAt
      ? `Updated ${format(new Date(resolvedTimestamp), 'PPp')}`
      : `Created ${format(new Date(resolvedTimestamp), 'PPp')} · no updates recorded yet`
    : 'No available date';

  const rowClasses = clsx(
    // Base styles
    'group',
    'border-b',
    'border-gray-200',
    'hover:bg-gray-50',
    'transition-colors',
    'duration-150',
    'cursor-pointer',
    
    // Selected state
    selected && 'bg-blue-50 border-blue-200',
    
    // Unread state - bolder text
    unread && 'font-semibold',
    
    // Hover state
    'hover:shadow-sm',
    
    className
  );

  return (
    <>
      <div
        className={rowClasses}
        onClick={handleRowClick}
        data-testid={testId}
        role="row"
        tabIndex={0}
        aria-label={`Ticket ${ticket.id}: ${ticket.subject}`}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleRowClick(e as any);
          }
        }}
        {...props}
      >
        <div className="flex items-start space-x-4 py-3 px-4">
          {/* Left Section - Selection & Controls */}
          <div className="flex items-center space-x-3 flex-shrink-0">
            {/* Selection Checkbox */}
            <input
              type="checkbox"
              checked={selected}
              onChange={handleSelectToggle}
              className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              aria-label={`Select ticket ${ticket.id}`}
            />

            {/* Priority Indicator */}
            <div className="w-3 flex justify-center">
              {getPriorityIndicator(ticket.priority)}
            </div>
          </div>

          {/* Middle Section - Sender & Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              {/* Sender Info */}
              <div className="flex items-center space-x-3 flex-shrink-0">
                <Avatar
                  name={ticket.requester.fullName}
                  size="sm"
                  src={ticket.requester.profileImageUrl}
                />
                <div className="min-w-0">
                  <p className={clsx(
                    'text-sm truncate',
                    unread ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'
                  )}>
                    {ticket.subject}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    from {ticket.requester.fullName}
                    {ticket.company && ` · ${ticket.company.name}`}
                  </p>
                </div>
              </div>

              {/* Right Section - Meta Info */}
              <div className="flex items-center space-x-4 flex-shrink-0">
                {/* Attachment Indicator */}
                {showAttachmentIndicator && ticket.attachments && ticket.attachments.length > 0 && (
                  <HiPaperClip className="w-4 h-4 text-gray-400" title={`${ticket.attachments.length} attachments`} />
                )}

                {/* Status Badge - Show all statuses including Open (BH#117) */}
                <Badge 
                  variant={getStatusBadgeVariant(ticket.status)} 
                  size="sm"
                >
                  {ticket.status === TicketStatus.InProgress ? 'In Progress' : ticket.status}
                </Badge>

                {/* Source Icon */}
                {getSourceIcon(ticket.source)}

                {/* Time */}
                <div className="text-sm text-gray-500 min-w-16 text-right" title={resolvedTimestampLabel}>
                  {formatInboxTime(resolvedTimestamp)}
                </div>

                {/* Threading Expand Button */}
                {threadingEnabled && relatedTickets.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleExpandToggle}
                    className="p-1"
                  >
                    {isExpanded ? (
                      <HiChevronDown className="w-4 h-4" />
                    ) : (
                      <HiChevronRight className="w-4 h-4" />
                    )}
                  </Button>
                )}
              </div>
            </div>

            {/* Ticket ID & Preview */}
            <div className="mt-1">
              <div className="flex items-center space-x-2">
                <span className="text-xs font-medium text-gray-500">#{ticket.ticketNumber || ticket.id}</span>
              </div>
              
              {showPreview && ticket.description && (
                <div className={clsx(
                  'text-sm text-gray-600 truncate mt-1 max-w-2xl',
                  unread ? 'font-medium' : 'font-normal'
                )}>
                  <RichTextRenderer
                    content={ticket.description}
                    htmlContent={ticket.descriptionHtml}
                    className="truncate"
                    maxLength={150}
                  />
                </div>
              )}

              {/* Tags - Show first few */}
              {ticket.tags && ticket.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {ticket.tags.slice(0, 2).map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-600"
                    >
                      {tag}
                    </span>
                  ))}
                  {ticket.tags.length > 2 && (
                    <span className="text-xs text-gray-400">
                      +{ticket.tags.length - 2}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Threaded/Related Tickets - Expanded View */}
      {threadingEnabled && isExpanded && relatedTickets.length > 0 && (
        <div className="bg-gray-50 border-b border-gray-200">
          <div className="pl-16 pr-4 py-2">
            <h4 className="text-xs font-medium text-gray-700 mb-2">
              Related Conversations ({relatedTickets.length})
            </h4>
            <div className="space-y-1">
              {relatedTickets.map((relatedTicket) => (
                <div
                  key={relatedTicket.id}
                  className="flex items-center space-x-3 py-1 px-2 hover:bg-white rounded cursor-pointer"
                  onClick={() => onClick?.(relatedTicket)}
                >
                  <Avatar
                    name={relatedTicket.requester?.fullName}
                    size="xs"
                    src={relatedTicket.requester?.profileImageUrl}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-700 truncate">
                      #{relatedTicket.id} - {relatedTicket.subject}
                    </p>
                  </div>
                  <span className="text-xs text-gray-500">
                    {formatInboxTime(resolveDisplayTimestamp(relatedTicket.updatedAt, relatedTicket.createdAt))}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
